import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function About() {
  useSEO({
    title: 'About Antalya Eats - Restaurant Guide Antalya Turkey',
    description: 'Learn about Antalya Eats, your trusted guide to discovering the best restaurants in Antalya, Turkey. We curate authentic dining experiences featuring Turkish, international, and fusion cuisines for tourists and locals alike.',
    keywords: 'about Antalya Eats, restaurant guide Antalya, dining Antalya',
    canonical: '/about',
    ogTitle: 'About Antalya Eats - Your Restaurant Guide',
    ogDescription: 'Learn about Antalya Eats and our mission to help you discover the best restaurants in Antalya',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      'name': 'About Antalya Eats',
      'url': '{{SITE_URL}}/about',
      'description': 'Learn about Antalya Eats, your trusted guide to discovering the best restaurants in Antalya',
      'mainEntity': {
        '@type': 'Organization',
        'name': 'Antalya Eats',
        'url': '{{SITE_URL}}/',
        'description': 'Your ultimate guide to finding the best restaurants in Antalya',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Antalya',
          'addressCountry': 'TR'
        }
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b-4 border-black z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://public.readdy.ai/ai/img_res/46496767-ecf9-45a1-9f97-898f0428276b.png" 
              alt="Antalya Eats Logo" 
              className="w-8 h-8 object-contain image-rendering-pixelated"
              style={{ imageRendering: 'pixelated' }}
            />
            <span className="text-lg font-bold tracking-tight">ANTALYA EATS</span>
          </Link>
          <nav className="flex gap-4">
            <Link to="/" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">Home</Link>
            <Link to="/about" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">About</Link>
          </nav>
        </div>
      </header>

      {/* About Content */}
      <main>
      <section className="pt-20 pb-12 px-4">
        <div className="container mx-auto max-w-3xl">
          <h1 className="text-3xl font-bold mb-6 text-center">ABOUT ANTALYA EATS</h1>
          
          <article className="space-y-4">
            <div className="bg-white border-4 border-black p-4">
              <h2 className="text-xl font-bold mb-3">Our Mission</h2>
              <p className="text-base text-gray-700 leading-relaxed">
                <strong>Antalya Eats</strong> is your ultimate guide to discovering the <strong>best restaurants in Antalya</strong>. 
                We help tourists and locals find amazing dining experiences across <strong>Turkish, Burger, 
                Sushi, and Mexican cuisines</strong>.
              </p>
            </div>

            <div className="bg-white border-4 border-black p-4">
              <h2 className="text-xl font-bold mb-3">Why Choose Us?</h2>
              <ul className="space-y-2 text-base text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-xl text-green-500 mt-1"></i>
                  <span>Carefully curated restaurant selections</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-xl text-green-500 mt-1"></i>
                  <span>Honest ratings and reviews</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-xl text-green-500 mt-1"></i>
                  <span>Easy-to-use category navigation</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-xl text-green-500 mt-1"></i>
                  <span>Updated information and contact details</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border-4 border-black p-4">
              <h2 className="text-xl font-bold mb-3">Explore Antalya</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-3">
                <strong>Antalya</strong> is a beautiful coastal city on Turkey's Mediterranean coast, known for its 
                stunning beaches, historic old town (Kaleiçi), and <strong>diverse culinary scene</strong>. Whether 
                you're craving traditional Turkish dishes, international flavors, or fusion cuisine, 
                Antalya has something for everyone.
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                Our directory makes it easy to find the perfect restaurant for any occasion, from 
                casual beachside dining to upscale fine dining experiences.
              </p>
            </div>

            <div className="bg-white border-4 border-black p-4">
              <h2 className="text-xl font-bold mb-3">Contact Us</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-3">
                Have a restaurant recommendation or feedback? We'd love to hear from you!
              </p>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <i className="ri-mail-line text-xl"></i>
                  <span className="text-base">info@antalyaeats.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-xl"></i>
                  <span className="text-base">Antalya, Turkey</span>
                </div>
              </div>
            </div>
          </article>

          {/* Back Button */}
          <div className="mt-8 text-center">
            <Link 
              to="/"
              className="inline-block bg-black hover:bg-gray-800 text-white font-bold text-base px-6 py-3 border-4 border-black transition-colors whitespace-nowrap cursor-pointer"
            >
              ← BACK TO HOME
            </Link>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 border-t-4 border-black">
        <div className="container mx-auto text-center">
          <p className="text-sm mb-2">© 2025 Antalya Eats. Your guide to the best restaurants in Antalya.</p>
          <a href="https://readdy.ai/?ref=logo" className="text-xs text-gray-400 hover:text-white whitespace-nowrap">Web Design</a>
        </div>
      </footer>
    </div>
  );
}
